import { get, isEmpty } from 'lodash';

import {
  fertilityProductSubstanceTypeNames,
  substanceThreshholds
} from 'constants/products';
import { applicationTypes } from 'constants/scripts';
import { resourceType } from 'constants/loading';

import { 
  parseApplicationProducts,
  parseFertilityChemicalTypes,
  parseFertilizerSubstanceTypes
} from 'products/productsParser';
import { setApplicationProducts } from 'products';

import { getProductById } from 'products/selectors';
import {
  getChemicalApplicationTypes,
  getChemicalTypeUuidByApplicationType,
  getFertilizersForSubstanceTypeUuid,
  getProtectionsForChemicalTypeUuid,
  getSubstanceTypesForApplicationType,
  getNitrapyrinApplication,
  substanceTypeIsLime
} from 'products/fertility-selectors';
import { getActiveUserId, getActiveFieldCountryCode } from 'current/selectors';

import {
  fetchChemicalById,
  fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid,
  fetchFertilityChemicalTypes,
  fetchFertilizerSubstanceTypes,
  fetchSeedByLegacyId,
  fetchSeedBySproutUuid
} from 'products/loaders';
import {
  processApplicationProducts,
  processFertilityChemicalTypes,
  processFertilizerSubstanceTypes
} from 'products/processors';

import { findResource } from 'loading/finders';
import { isUuid } from 'util/id';

export const findFertilizerSubstanceTypes = ({
  dispatch,
  getState,
  forceReload = false,
  forceWait = false,
  onError
}) => {
  const userId = getActiveUserId(getState());
  // TODO: Build in dependency to types by calling find()?

  const resourceSpec = {
    type: resourceType.FERTILITY_CHEMICAL_SUBSTANCES,
    selector: () =>
      getSubstanceTypesForApplicationType(
        getState(),
        applicationTypes.FERTILIZER
      ),
    fetcher: () => fetchFertilizerSubstanceTypes({ userId }),
    processor: (raw) =>
      processFertilizerSubstanceTypes({
        dispatch,
        getState,
        fertilizerSubstanceTypes: parseFertilizerSubstanceTypes(raw)
      })
  };

  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    onError,
    ...resourceSpec
  });
};

export const findFertilizersForSubstanceTypeUuid = ({
  dispatch,
  getState,
  substanceTypeUuid
}) => {
  return Promise.resolve() // force a thenable wrapper
  .then(() => {
    const state = getState();
    const countryCode = getActiveFieldCountryCode(state);

    const fertilizers = getFertilizersForSubstanceTypeUuid(
      state,
      substanceTypeUuid
    );
    if (!isEmpty(fertilizers)) {
      return fertilizers;
    }

    const substanceTypes = getSubstanceTypesForApplicationType(
      state,
      applicationTypes.FERTILIZER
    );
    const applicationType = applicationTypes.FERTILIZER;
    const chemicalTypeUuid = getChemicalTypeUuidByApplicationType(
      state,
      applicationType
    );
    const userId = getActiveUserId(state);
    let minPercent;
    if (substanceTypeUuid && substanceTypeIsLime(state, substanceTypeUuid)) {
      minPercent = get(
        substanceThreshholds[fertilityProductSubstanceTypeNames.LIME],
        'MIN'
      );
    }

    function retrieveAllFertilizers() {
      const promises = [];
      if (substanceTypes.byUuid) {
        Object.keys(substanceTypes.byUuid).forEach((uuid) => {
          const specificFertilizers = getFertilizersForSubstanceTypeUuid(
            state,
            uuid
          );
            // We include 'substanceTypeUuid !== uuid' because we actually only want 3 promises here -->
            // we are going to make THE 4th request via the Return of this function
            // We need fertilizers, & we can have just one specificFertilizer on app-load for the /edit route
          if (
            substanceTypeUuid !== uuid
              && (!specificFertilizers || specificFertilizers.length === 1)
          ) {
            promises.push(
              fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid({
                chemicalTypeUuid,
                minPercent,
                substanceTypeUuid: uuid,
                userId,
                countryCode
              })
            );
          }
        });
      }

      return Promise.all(promises);
    }

    retrieveAllFertilizers()
    .then((results) => {
      const uuids = [];
      if (substanceTypes.byUuid) {
        Object.keys(substanceTypes.byUuid).forEach((uuid) => {
          if (substanceTypeUuid !== uuid) {
            // no result for the selected substance type, that query comes in the return
            uuids.push(uuid);
          }
        });
      }
      results.forEach((result, i) => {
        const parsedProducts = parseApplicationProducts({
          applicationType,
          rawProducts: result
        });
        dispatch(
          setApplicationProducts({
            applicationType,
            products: parsedProducts,
            substanceTypeUuid: uuids[i]
          })
        );
      });
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.log(err);
    });

    if (!substanceTypeUuid) {
      return [];
    }

    return fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid({
      chemicalTypeUuid,
      minPercent,
      substanceTypeUuid,
      userId,
      countryCode
    }).then((rawProducts) =>
      processApplicationProducts({
        applicationType,
        dispatch,
        getState,
        rawProducts,
        substanceTypeUuid
      })
    );
  });
};

export const findProtectionsForChemicalTypeUuid = ({
  dispatch,
  getState,
  chemicalTypeIds
}) => {
  return Promise.resolve() // force a thenable wrapper
  .then(() => {
    const state = getState();
    const countryCode = getActiveFieldCountryCode(state);

    const fertilizers = getProtectionsForChemicalTypeUuid(
      state,
      Object.values(chemicalTypeIds)[0]
    );
    if (!isEmpty(fertilizers)) {
      return fertilizers;
    }

    const applicationType = applicationTypes.APPLICATION;
    const userId = getActiveUserId(state);

    function retrieveAllCPFertilizers() {
      const promises = [];
      Object.values(chemicalTypeIds).forEach((chemicalTypeUuid) => {
        promises.push(
          fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid({
            chemicalTypeUuid,
            userId,
            countryCode
          })
        );
      });

      return Promise.all(promises);
    }

    retrieveAllCPFertilizers()
    .then((results) => {
      const uuids = [];
      Object.values(chemicalTypeIds).forEach((uuid) => {
        uuids.push(uuid);
      });
      results.forEach((result, i) => {
        dispatch(
          setApplicationProducts({
            applicationType,
            products: result.results,
            chemicalTypeUuid: uuids[i]
          })
        );
      });
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.log(err);
    });
    return [];
  });
};

export const findFertilityStabilizers = ({
  dispatch,
  getState,
  forceReload = false,
  forceWait = false,
  onError
}) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const countryCode = getActiveFieldCountryCode(state);
  const applicationType = applicationTypes.STABILIZER;
  // TODO: Build in dependency to types by calling find()?
  const chemicalTypeUuid = getChemicalTypeUuidByApplicationType(
    state,
    applicationType
  );
  const resourceSpec = {
    type: resourceType.FERTILITY_CHEMICAL_STABILIZERS,
    selector: () => getNitrapyrinApplication(state),
    fetcher: () =>
      fetchChemicalsForChemicalTypeUuidAndSubstanceTypeUuid({
        chemicalTypeUuid,
        userId,
        countryCode
      }),
    processor: (rawProducts) =>
      processApplicationProducts({
        applicationType,
        dispatch,
        getState,
        rawProducts
      })
  };

  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    onError,
    ...resourceSpec
  });
};

export const findFertilityChemicalTypes = ({
  dispatch,
  getState,
  forceReload = false,
  forceWait = false,
  onError
}) => {
  const userId = getActiveUserId(getState());
  const resourceSpec = {
    type: resourceType.FERTILITY_CHEMICAL_TYPES,
    selector: () => getChemicalApplicationTypes(getState()),
    fetcher: () => fetchFertilityChemicalTypes({ userId }),
    processor: (raw) =>
      processFertilityChemicalTypes({
        dispatch,
        getState,
        fertilityChemicalTypes: parseFertilityChemicalTypes(raw)
      })
  };

  return findResource({
    dispatch,
    getState,
    forceReload,
    forceWait,
    onError,
    ...resourceSpec
  });
};

export const findProductById = ({
  applicationType,
  dispatch,
  getState,
  id
}) => {
  return Promise.resolve() // force a thenable wrapper
  .then(() => {
    const state = getState();
    const product = getProductById({ applicationType, id, state });
    const fetchSeedFromSprout = isUuid(id) ? fetchSeedBySproutUuid : fetchSeedByLegacyId;

    if (product) {
      return product;
    }

    const userId = getActiveUserId(state);

    const processAfterFetch = (rawProducts) => {
      // Check to see if product was fetched by another execution path during the time it took for fetch to complete.
      const product = getProductById({
        applicationType,
        id,
        state: getState()
      });
      return product
        ? [product]
        : processApplicationProducts({
          applicationType,
          dispatch,
          getState,
          rawProducts
        });
    };

    switch (applicationType) {
      case applicationTypes.FERTILIZER:
      case applicationTypes.APPLICATION:
        return fetchChemicalById({ id, userId })
        .then((rawProducts) => processAfterFetch(rawProducts))
        .then((product) => product[0]);
      case applicationTypes.SEED:
        return fetchSeedFromSprout(id)
        .then(processAfterFetch).then((product) => product[0]);
      default:
        return null;
    }
  });
};
